import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ReactNode, useState } from 'react';

export interface DataTableProps<TRow> {
  columns: ColumnDef<TRow, unknown>[];
  data: TRow[];
  /** Shown in a full-width row when `data` is empty. */
  emptyMessage: ReactNode;
  /** Stable row identity — falls back to the row index when omitted. */
  getRowId?: (row: TRow) => string;
  className?: string;
  /** Applied to every `<tr>` in the body; the table itself stays unstyled. */
  rowClassName?: string;
  rowStyle?: React.CSSProperties;
  headerRowStyle?: React.CSSProperties;
  cellStyle?: React.CSSProperties;
  headerCellStyle?: React.CSSProperties;
  /** Column sorting is opt-in so read-only tables stay keyboard-inert. */
  enableSorting?: boolean;
  initialSorting?: SortingState;
}

/**
 * A thin presentation layer over TanStack Table.
 *
 * TanStack Table is headless: it owns the row model, sorting and column
 * definitions; every element below is ours, so existing CSS keeps working. The
 * value is that sort state, comparator selection and header/cell rendering are
 * library code that is exercised by its own test suite instead of being
 * re-derived in each page.
 */
export default function DataTable<TRow>({
  columns,
  data,
  emptyMessage,
  getRowId,
  className,
  rowClassName,
  rowStyle,
  headerRowStyle,
  cellStyle,
  headerCellStyle,
  enableSorting = false,
  initialSorting = [],
}: DataTableProps<TRow>) {
  const [sorting, setSorting] = useState<SortingState>(initialSorting);

  const table = useReactTable({
    data,
    columns,
    state: enableSorting ? { sorting } : undefined,
    onSortingChange: setSorting,
    enableSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getRowId: getRowId ? (row) => getRowId(row) : undefined,
  });

  const rows = table.getRowModel().rows;

  return (
    <table className={className}>
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id} style={headerRowStyle}>
            {headerGroup.headers.map((header) => {
              const sortable = enableSorting && header.column.getCanSort();
              const direction = header.column.getIsSorted();
              return (
                <th
                  aria-sort={
                    !sortable || !direction
                      ? undefined
                      : direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                  }
                  key={header.id}
                  style={headerCellStyle}
                >
                  {sortable ? (
                    <button
                      className="data-table-sort"
                      type="button"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <span aria-hidden="true">
                        {direction === 'asc' ? ' ▲' : direction === 'desc' ? ' ▼' : ''}
                      </span>
                    </button>
                  ) : (
                    flexRender(header.column.columnDef.header, header.getContext())
                  )}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td className="data-table-empty" colSpan={columns.length} style={cellStyle}>
              {emptyMessage}
            </td>
          </tr>
        ) : (
          rows.map((row) => (
            <tr className={rowClassName} key={row.id} style={rowStyle}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} style={cellStyle}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  );
}
